import { useEffect, useState, useCallback } from 'react';
import { api } from '../../api';
import '../../pages.css';

// Upload the file straight to R2 using a presigned URL (with progress).
function putToR2(url, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error('上传失败 ' + xhr.status)));
    xhr.onerror = () => reject(new Error('上传失败（网络错误）'));
    xhr.send(file);
  });
}

export default function VideoManager() {
  const [videos, setVideos] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const loadVideos = useCallback(async () => {
    try {
      const data = await api.videos();
      setVideos(data.videos || []);
    } catch (err) {
      setError(err.message || '视频列表加载失败');
    }
  }, []);

  useEffect(() => { loadVideos(); }, [loadVideos]);

  async function onUpload(e) {
    e.preventDefault();
    if (!file) { setError('请选择视频文件'); return; }
    if (!title.trim()) { setError('请填写标题'); return; }
    setError('');
    setUploading(true);
    setProgress(0);
    try {
      // 1. Presigned PUT from the Worker.
      const { key, uploadUrl } = await api.uploadUrl(file.name, file.type, 'videos');
      // 2. Browser uploads directly to R2.
      await putToR2(uploadUrl, file, setProgress);
      // 3. Metadata into D1.
      await api.createVideo({
        title: title.trim(),
        description,
        r2Key: key,
        contentType: file.type,
        sizeBytes: file.size,
      });
      setTitle('');
      setDescription('');
      setFile(null);
      await loadVideos();
    } catch (err) {
      setError(err.message || '上传失败');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }

  async function onDelete(v) {
    if (!window.confirm('删除该视频？')) return;
    try {
      await api.deleteVideo(v.id);
      setVideos((prev) => prev.filter((x) => x.id !== v.id));
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="admin-section">
      <h2>上传视频</h2>
      <form onSubmit={onUpload}>
        <div className="field-row"><label>标题</label><input value={title} onChange={(e) => setTitle(e.target.value)} /></div>
        <div className="field-row"><label>描述（可选）</label><textarea value={description} onChange={(e) => setDescription(e.target.value)} /></div>
        <div className="field-row"><label>视频文件</label><input type="file" accept="video/*" onChange={(e) => setFile(e.target.files[0])} /></div>
        {uploading && <p>上传中 {progress}%</p>}
        {error && <p className="error">{error}</p>}
        <button className="btn" type="submit" disabled={uploading}>上传</button>
      </form>

      <h2>视频列表 ({videos.length})</h2>
      <table className="admin-table">
        <thead><tr><th>标题</th><th>大小</th><th>上传时间</th><th></th></tr></thead>
        <tbody>
          {videos.map((v) => (
            <tr key={v.id}>
              <td>{v.title}</td>
              <td>{v.sizeBytes ? (v.sizeBytes / 1048576).toFixed(1) + ' MB' : ''}</td>
              <td>{v.uploadedAt ? new Date(v.uploadedAt).toLocaleString() : ''}</td>
              <td><button className="btn secondary" onClick={() => onDelete(v)}>删除</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
